const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;
const DEBOUNCE_DELAY = 300;

const productHandler = () => ({
    // Stan początkowy
    productId: null,
    images: [],
    parentImages: [],
    currentImage: '',
    currentImageIndex: 0,
    variantName: '',
    description: '',
    availableAttributes: {},
    selectedAttributes: {},
    variantAttributes: [],
    sku: '',
    ean: '',
    prices: {
        regular_price: { formatted: '', raw: 0 },
        sale_price: { formatted: '', raw: 0 },
        on_sale: false,
        saving_percent: 0
    },
    // v1.5-F1: pusty initial state. Tekst zapełnia stock-eta:updated listener (REST /breakdown),
    // żeby NIE było skoku "Dostępny → Zamów do 17:00 dostawa 9 → Wysyłka pn 8 czerwca".
    stockStatus: {
        text: '',
        class: ''
    },
    variationsMap: [],
    loading: false,
    selectedVariationId: null,
    taxRate: 0,

    // Stan dla produktów kablowych
    activeTab: 'full',
    cableLength: 1,
    basePrice: 0,
    isCableProduct: false,
    maxCableLength: 305,

    // 2026-06-17: clamp custom length do dostępnego stocka (mb).
    // availableMeters aktualizowany przez stock-eta:updated listener z e.detail.breakdown.total
    // przy unit='mb'. lastCableClampNotificationAt — throttle 2s na toast, ten sam pattern co
    // addToCartHandler.lastClampNotificationAt.
    availableMeters: 0,
    lastCableClampNotificationAt: 0,

    // Optymalizacja
    imageCache: new Map(),
    pendingRequests: new Map(),

    init() {
        // Tworzymy globalny store z danymi produktu dla Alpine
        if (window.Alpine) {
            window.Alpine.store('productData', {
                availableAttributes: this.availableAttributes,
                selectedAttributes: this.selectedAttributes,
                variationsMap: this.variationsMap
            });

            // Aktualizujemy store przy zmianie danych
            window.addEventListener('attributes:changed', (e) => {
                window.Alpine.store('productData').selectedAttributes = e.detail.attributes || {};
            });

            window.addEventListener('product:initialized', (e) => {
                if (e.detail.attributes) {
                    window.Alpine.store('productData').availableAttributes = e.detail.attributes;
                }
                if (e.detail.variations) {
                    window.Alpine.store('productData').variationsMap = e.detail.variations;
                }
            });
        }

        this.setupKeyboardNavigation();
        this.setupEventListeners();

        const productContainer = document.getElementById('product-container');
        if (productContainer) {
            // Dodajemy odczyt stawki podatku
            this.taxRate = parseFloat(productContainer.getAttribute('data-tax-rate')) || 0;
            this.isCableProduct = productContainer.hasAttribute('data-is-cable');
            this.maxCableLength = parseInt(productContainer.getAttribute('data-cable-length') || '305');
            this.sku = productContainer.getAttribute('data-parent-sku') || '';
            this.ean = productContainer.getAttribute('data-parent-ean') || '';

            const parentImagesData = productContainer.getAttribute('data-parent-images');
            if (parentImagesData) {
                this.parentImages = JSON.parse(parentImagesData);
                this.images = this.parentImages;
                if (this.images.length > 0) {
                    this.setCurrentImage(this.images[0].large);
                }
            }
        }
    },

    calculateNettoPrice(grossPrice) {
        if (!grossPrice || this.taxRate === 0) return 0;
        return grossPrice / (1 + this.taxRate / 100);
    },

    setupEventListeners() {
        window.addEventListener('popstate', () => this.handlePopState());
        window.addEventListener('cable:lengthChanged', (e) => {
            if (e.detail?.length) {
                this.cableLength = e.detail.length;
                this.updateCablePrice();
            }
        });

        // Nasłuchuj na zdarzenia z komponentu selektora wariantów
        window.addEventListener('variant-selector:attributeSelected', (e) => {
            if (e.detail && e.detail.attributeName && e.detail.value !== undefined) {
                this.selectAttribute(e.detail.attributeName, e.detail.value);
            }
        });

        // Slice 1C v1.3 — sync z naszym stockEtaHandler (z REST /breakdown)
        // Nadpisuje statyczny tekst "Zamów do 17:00, dostawa 9 czerwca" naszym dynamicznym
        // komunikatem "⚡ Szybka wysyłka pn 8 czerwca (dostawa wt 9 czerwca)" itp.
        window.addEventListener('stock-eta:updated', (e) => {
            if (e.detail && e.detail.message) {
                this.stockStatus = {
                    ...this.stockStatus,
                    text: e.detail.message,
                    subtext: e.detail.subtitle || '',
                    // v1.8-F3: dodajemy kopię `class` z REST (zielona/czerwona pillkka obramówki).
                    // Bez tego initial stockStatus.class='' nigdy się nie wypełnia → brak obramówki.
                    class: e.detail.class || this.stockStatus.class,
                    in_stock: e.detail.allowsAddToCart,
                    stock_quantity: e.detail.maxQuantity,
                };
            }
            // 2026-06-17: dla kabla czytaj dostępne mb z breakdown — używane w
            // validateAndUpdateLength do klampa custom length do realnego stocka.
            // Hotfix: po update availableMeters re-clamp cableLength jeśli jest > nowego limitu
            // (race condition: REST wraca po wpisaniu wartości). Wzorzec jak addToCartHandler
            // robi dla quantity przez clampQuantityWithNotice w tym samym listenerze.
            if (e.detail?.breakdown?.unit === 'mb' && typeof e.detail.breakdown.total === 'number') {
                this.availableMeters = e.detail.breakdown.total;
                this.clampCableLengthWithNotice();
            }
        });
    },

    setupKeyboardNavigation() {
        document.addEventListener('keydown', (e) => {
            if (!this.loading) {
                switch (e.key) {
                    case 'ArrowLeft':
                        this.navigateGallery('prev');
                        break;
                    case 'ArrowRight':
                        this.navigateGallery('next');
                        break;
                }
            }
        });
    },

    // Metody dla kabla
    calculatePricePerMeter() {
        return this.basePrice / this.maxCableLength;
    },

    calculateCustomPrice() {
        const pricePerMeter = this.calculatePricePerMeter();
        return pricePerMeter * this.cableLength;
    },

    validateAndUpdateLength(value) {
        // 2026-06-17: w trybie 'full' cableLength jest sterowany hardcoded (305) przez changeTab.
        // Tu klampujemy TYLKO „zamów na wymiar". Hubert: nie tykać kup cały kabel.
        if (this.activeTab !== 'custom') return;

        const length = parseFloat(value);
        if (isNaN(length)) {
            this.cableLength = 1;
            this.updateCablePrice();
            window.dispatchEvent(new CustomEvent('cable:lengthChanged', { detail: { length: 1 } }));
            return;
        }
        // Ustaw na surową wartość; helper sklampuje jeśli > stockCap i sam dispatchuje cable:lengthChanged + toast.
        this.cableLength = Math.max(length, 1);
        if (!this.clampCableLengthWithNotice()) {
            // Nie klampował — emituj zwykły update.
            this.updateCablePrice();
            window.dispatchEvent(new CustomEvent('cable:lengthChanged', {
                detail: { length: this.cableLength }
            }));
        }
    },

    // 2026-06-17: wydzielenie z validateAndUpdateLength żeby reuse w stock-eta:updated listenerze.
    // Wzorzec analogiczny do addToCartHandler.clampQuantityWithNotice — reaktywny re-clamp
    // gdy maxLength/maxQuantity przychodzi z REST już po wpisaniu wartości przez klienta.
    // Wywołanie z listenera jest bezpieczne (sam sprawdza guardy: isCableProduct, activeTab,
    // stockCap > 0, cableLength > stockCap).
    clampCableLengthWithNotice() {
        if (!this.isCableProduct || this.activeTab !== 'custom') return false;
        const stockCap = this.availableMeters > 0
            ? Math.min(this.maxCableLength, Math.floor(this.availableMeters))
            : this.maxCableLength;
        if (stockCap <= 0) return false;
        if (this.cableLength <= stockCap) return false;

        this.cableLength = stockCap;
        const now = Date.now();
        if (now - this.lastCableClampNotificationAt > 2000) {
            this.lastCableClampNotificationAt = now;
            window.dispatchEvent(new CustomEvent('show-notification', {
                detail: {
                    message: `Maksymalna długość: ${stockCap} m`,
                    type: 'warning'
                }
            }));
        }
        this.updateCablePrice();
        window.dispatchEvent(new CustomEvent('cable:lengthChanged', {
            detail: { length: this.cableLength }
        }));
        return true;
    },

    changeTab(tab) {
        this.activeTab = tab;
        this.updateCablePrice();

        // Emituj zdarzenie o zmianie typu kabla
        window.dispatchEvent(
            new CustomEvent('cable:typeChanged', {
                detail: {
                    type: tab
                }
            })
        );
    },

    updateCablePrice() {
        if (!this.isCableProduct) return;

        const price = this.activeTab === 'full' ? this.basePrice : this.calculateCustomPrice();
        const nettoPrice = this.calculateNettoPrice(price);

        this.prices = {
            regular_price: {
                formatted: this.formatPrice(price),
                raw: price,
                netto: this.formatPrice(nettoPrice)
            },
            on_sale: false
        };

        window.dispatchEvent(new CustomEvent('price:updated', {
            detail: {
                prices: this.prices,
                cableLength: this.cableLength,
                isCustomLength: this.activeTab === 'custom',
                taxRate: this.taxRate
            }
        }));
    },

    // Metody dla galerii
    navigateGallery(direction) {
        if (this.images.length <= 1) return;
        const newIndex =
            direction === 'next'
                ? (this.currentImageIndex + 1) % this.images.length
                : (this.currentImageIndex - 1 + this.images.length) % this.images.length;
        const img = this.images[newIndex];
        this.setCurrentImage(img.full || img.large);
    },

    // Inicjalizacja produktu
    async initProduct(productId) {
        if (!window.wpApiSettings) {
            window.wpApiSettings = {
                root: window.wcSettings?.restUrl || '/wp-json/',
                nonce: window.wcSettings?.nonce || ''
            };
        }

        this.productId = productId;
        this.loading = true;

        try {
            const response = await this.fetchWithRetry(
                `${window.wpApiSettings.root}wc/v3/custom/product/${productId}/attributes`,
                {
                    credentials: 'same-origin',
                    headers: { 'X-WP-Nonce': window.wpApiSettings.nonce }
                }
            );

            if (!response || !response.attributes) {
                throw new Error('Nieprawidłowe dane produktu');
            }

            this.availableAttributes = response.attributes;
            this.variationsMap = response.variations || [];
            this.sortAttributeValues();

            // Check URL parameters first
            this.checkUrlParameters();

            // If no attributes are selected from URL, select first available variant
            if (Object.keys(this.selectedAttributes).length === 0) {
                setTimeout(() => {
                    this.selectFirstAvailableVariant();
                }, 0);
            }

            this.preloadImages();

            window.dispatchEvent(
                new CustomEvent('product:initialized', {
                    detail: {
                        productId: this.productId,
                        isCableProduct: this.isCableProduct,
                        prices: this.prices,
                        stockStatus: this.stockStatus,
                        attributes: this.availableAttributes,
                        variations: this.variationsMap
                    }
                })
            );
        } catch (error) {
            console.error('Error initializing product:', error);
            window.dispatchEvent(
                new CustomEvent('show-notification', {
                    detail: {
                        message: 'Nie udało się załadować danych produktu',
                        type: 'error'
                    }
                })
            );
        } finally {
            this.loading = false;
        }
    },

    // New method to select first available variant
    async selectFirstAvailableVariant() {
        if (!this.variationsMap.length) return;

        // 2026-06-11: preferuj wariant z kablowo_total>0; fallback do variationsMap[0].
        const firstVariation = this.findFirstAvailableVariation() || this.variationsMap[0];

        // Set all attributes from the first variation
        for (const [attributeName, value] of Object.entries(firstVariation.attributes)) {
            this.selectedAttributes[attributeName] = value;
        }

        // Update URL with selected attributes
        this.updateUrl();

        // Fetch and update variant data
        if (this.isCompleteVariation()) {
            await this.fetchVariantData(firstVariation.variation_id);
        }
    },

    initSimpleProduct(initialData) {
        this.productId = initialData.productId;
        this.images = initialData.images || [];
        this.parentImages = initialData.images || [];
        this.setCurrentImage(this.images[0]?.large || '');
        this.prices = initialData.prices;
        this.sku = initialData.sku || this.sku;
        this.ean = initialData.ean || this.ean;
        this.basePrice = parseFloat(initialData.prices.regular_price.raw) || 0;
        this.updateStockStatus(initialData.stock_status);
        this.preloadImages();

        if (this.isCableProduct) {
            this.activeTab = 'full';
            this.updateCablePrice();
        }

        window.dispatchEvent(new CustomEvent('product:initialized', {
            detail: {
                productId: this.productId,
                isCableProduct: this.isCableProduct,
                prices: this.prices,
                stockStatus: initialData.stock_status
            }
        }));
    },

    // Obsługa wariantów
    async selectAttribute(attributeName, value) {
        try {
            const previousAttributes = { ...this.selectedAttributes };

            if (this.selectedAttributes[attributeName] === value) {
                delete this.selectedAttributes[attributeName];
            } else {
                if (!this.isAttributeValueAvailable(attributeName, value)) {
                    this.selectedAttributes = { [attributeName]: value };
                } else {
                    this.selectedAttributes[attributeName] = value;
                }
            }

            this.updateUrl();

            if (this.isCompleteVariation()) {
                const selectedVariation = this.findSelectedVariation();
                if (selectedVariation) {
                    await this.fetchVariantData(selectedVariation.variation_id);
                }
            } else {
                this.resetToParentImages();
            }

            this.updateAddToCartButton();

            window.dispatchEvent(new CustomEvent('attributes:changed', {
                detail: {
                    attributes: this.selectedAttributes,
                    isComplete: this.isCompleteVariation(),
                    selectedVariationId: this.selectedVariationId
                }
            }));

        } catch (error) {
            console.error('Error selecting attribute:', error);
            window.dispatchEvent(new CustomEvent('show-notification', {
                detail: {
                    message: 'Wystąpił błąd podczas wyboru opcji',
                    type: 'error'
                }
            }));
            this.selectedAttributes = previousAttributes;
        }
    },

    isAttributeValueAvailable(attributeName, value) {
        // 2026-06-11: wariant musi pasować do wybranych atrybutów ORAZ mieć
        // kablowo_total > 0 (suma Szczecin + Lanberg). Wcześniej sprawdzaliśmy
        // tylko czy istnieje wariant z tą kombinacją - klient mógł wybrać 0+0
        // i dostawał disabled button bez powodu.
        const testSelection = {...this.selectedAttributes, [attributeName]: value};
        return this.variationsMap.some(variation => {
            const matches = Object.entries(testSelection).every(([name, val]) =>
                variation.attributes[name] === val
            );
            if (!matches) return false;
            // kablowo_total pochodzi z variant-functions.php - jeśli backend nie zwrócił
            // (stara wersja), traktujemy jak dostępny (graceful fallback).
            if (typeof variation.kablowo_total === 'number') {
                return variation.kablowo_total > 0;
            }
            return variation.is_in_stock !== false;
        });
    },

    /**
     * 2026-06-11: znajdź pierwszy dostępny wariant pasujący do (opcjonalnie) zadanych atrybutów.
     * Używany przez auto-skip gdy URL/wybór wskazuje na wariant 0+0.
     */
    findFirstAvailableVariation(seedAttributes = {}) {
        return this.variationsMap.find(v => {
            const total = (typeof v.kablowo_total === 'number')
                ? v.kablowo_total
                : (v.is_in_stock === false ? 0 : 9999);
            if (total <= 0) return false;
            // jeśli podano seed, preferuj warianty pasujące do niego (np. ta sama długość)
            return Object.entries(seedAttributes).every(([name, val]) =>
                !val || v.attributes[name] === val
            );
        }) || this.variationsMap.find(v => {
            const total = (typeof v.kablowo_total === 'number')
                ? v.kablowo_total
                : (v.is_in_stock === false ? 0 : 9999);
            return total > 0;
        }) || null;
    },

    isSelected(attributeName, value) {
        return this.selectedAttributes[attributeName] === value;
    },

    isCompleteVariation() {
        return Object.keys(this.selectedAttributes).length ===
            Object.keys(this.availableAttributes).length;
    },

    findSelectedVariation() {
        return this.variationsMap.find(variation =>
            Object.entries(this.selectedAttributes).every(([name, value]) =>
                variation.attributes[name] === value
            )
        );
    },

    // Obsługa obrazów
    async fetchVariantData(variantId) {
        if (this.pendingRequests.has(variantId)) {
            return this.pendingRequests.get(variantId);
        }

        const request = (async () => {
            try {
                this.loading = true;
                const response = await this.fetchWithRetry(
                    `${window.wpApiSettings.root}wc/v3/custom/product/variant/${variantId}`,
                    {
                        credentials: 'same-origin',
                        headers: { 'X-WP-Nonce': window.wpApiSettings.nonce }
                    }
                );

                this.updateVariantData(response);
                return response;
            } finally {
                this.loading = false;
                this.pendingRequests.delete(variantId);
            }
        })();

        this.pendingRequests.set(variantId, request);
        return request;
    },

    updateVariantData(data) {
        this.selectedVariationId = data.variation_id;
        this.variantName = data.variant_name || ''; // Ustaw nazwę wariantu
        this.description = data.description || '';
        this.sku = data.sku || '';
        this.ean = data.ean || '';

        if (data.images && data.images.length > 0) {
            this.images = data.images;
        } else {
            this.images = [...this.parentImages];
        }

        this.currentImageIndex = 0;
        this.setCurrentImage(this.images[0]?.large || '');
        this.prices = data.prices;
        this.basePrice = data.prices.regular_price.raw;
        this.variantAttributes = data.attributes.map(attr => ({
            name: attr.name,
            value: attr.value,
            unit: attr.unit || '' // Dodajemy pole unit
        }));
        // v1.5-C4: usunięto updateStockStatus(data.stock_status) — single source of truth
        // dla stockStatus.text/subtext jest REST `/breakdown` przez stock-eta:updated listener
        // (linie 112-122). Stary WC native komunikat „Zamów do 17:00, dostawa wt 9 czerwca"
        // powodował T2 SKOK między T1 ('Dostępny') i T3 (nasz format z Slice 1B/1C).
        this.preloadImages();

        if (this.isCableProduct) {
            this.updateCablePrice();
        }

        this.updateAddToCartButton();

        window.dispatchEvent(new CustomEvent('variant:updated', {
            detail: {
                variantId: this.selectedVariationId,
                prices: this.prices,
                stockStatus: data.stock_status,
                attributes: this.variantAttributes,
                taxRate: this.taxRate // Dodajemy taxRate do zdarzenia
            }
        }));
        window.dispatchEvent(new CustomEvent('variant:selected', {
            detail: {
                variationId: this.selectedVariationId,
            }
        }));
    },

    updateAddToCartButton() {
        const addToCartButton = document.querySelector('[x-bind\\:data-product-id]');
        if (addToCartButton) {
            addToCartButton.setAttribute('data-product-id', this.selectedVariationId || this.productId);
            addToCartButton.setAttribute('data-product-name', this.variantName || '');
        }
    },


    resetToParentImages() {
        if (this.parentImages.length > 0) {
            this.images = [...this.parentImages];
            this.currentImageIndex = 0;
            this.setCurrentImage(this.images[0].large);
            this.preloadImages();
        }
        this.selectedVariationId = null;
        this.variantName = ''; // Reset nazwy wariantu
        window.dispatchEvent(new CustomEvent('variant:reset'));
    },


    setCurrentImage(src) {
        if (!src || typeof src !== 'string') return;

        requestAnimationFrame(() => {
            this.currentImage = src;
            this.currentImageIndex = this.images.findIndex(img => img.full === src || img.large === src);
            if (this.currentImageIndex === -1) this.currentImageIndex = 0;
            this.preloadAdjacentImages();
        });
    },

    // Zarządzanie obrazami
    preloadAdjacentImages() {
        if (this.images.length <= 1) return;
        const nextIndex = (this.currentImageIndex + 1) % this.images.length;
        const prevIndex = (this.currentImageIndex - 1 + this.images.length) % this.images.length;
        [nextIndex, prevIndex].forEach(index => {
            const imageUrl = this.images[index].large;
            if (!this.imageCache.has(imageUrl)) {
                this.loadImage(imageUrl);
            }
        });
    },

    preloadImages() {
        this.images.forEach(image => {
            if (!this.imageCache.has(image.large)) {
                this.loadImage(image.large);
            }
            if (!this.imageCache.has(image.thumbnail)) {
                this.loadImage(image.thumbnail);
            }
        });
    },

    loadImage(src) {
        if (!src || this.imageCache.has(src)) return;
        const img = new Image();
        img.onload = () => this.imageCache.set(src, img);
        img.onerror = () => console.error(`Failed to load image: ${src}`);
        img.src = src;
    },

    // Pomocnicze metody
    updateStockStatus(stockStatus) {
        if (!stockStatus) return;

        const currentTime = new Date();
        const cutoffTime = new Date();
        cutoffTime.setHours(17, 0, 0);

        const isBeforeCutoff = currentTime < cutoffTime;
        const currentDay = currentTime.getDay();

        const calculateDeliveryDate = (baseDate, additionalDays = 0) => {
            const deliveryDate = new Date(baseDate);
            let daysToAdd = additionalDays;

            while (daysToAdd > 0) {
                deliveryDate.setDate(deliveryDate.getDate() + 1);
                if (deliveryDate.getDay() !== 0 && deliveryDate.getDay() !== 6) {
                    daysToAdd--;
                }
            }

            while (deliveryDate.getDay() === 0 || deliveryDate.getDay() === 6) {
                deliveryDate.setDate(deliveryDate.getDate() + 1);
            }

            return deliveryDate;
        };

        const formatDate = (date) => {
            return date.toLocaleDateString('pl-PL', {
                day: 'numeric',
                month: 'long'
            });
        };

        const getOrderText = (isBeforeCutoff) => {
            if (isBeforeCutoff) {
                return "Zamów do 17:00, dostawa";
            } else {
                return "Zamów teraz, dostawa";
            }
        };

        let deliveryDate;
        if (currentDay === 5) { // Piątek
            if (isBeforeCutoff) {
                deliveryDate = calculateDeliveryDate(currentTime, 1);
            } else {
                deliveryDate = calculateDeliveryDate(currentTime, 2);
            }
        } else if (currentDay === 6) { // Sobota
            deliveryDate = calculateDeliveryDate(currentTime, 2);
        } else if (currentDay === 0) { // Niedziela
            deliveryDate = calculateDeliveryDate(currentTime, 2);
        } else { // Dni robocze (poniedziałek-czwartek)
            if (isBeforeCutoff) {
                deliveryDate = calculateDeliveryDate(currentTime, 1);
            } else {
                deliveryDate = calculateDeliveryDate(currentTime, 2);
            }
        }

        // Najpierw sprawdzamy czy produkt ma zerowy stan i jest dostępny na zamówienie
        if (stockStatus.backorders_allowed && (stockStatus.stock_quantity <= 0)) {
            const extendedDeliveryDate = calculateDeliveryDate(currentTime, 3);
            this.stockStatus = {
                text: 'Dostępny',
                subtext: `Dostawa do ${formatDate(extendedDeliveryDate)}`,
                class: 'text-green-600 bg-green-100'
            };
        }
        // Następnie sprawdzamy czy produkt jest dostępny w magazynie
        else if (stockStatus.in_stock && (stockStatus.stock_quantity > 0)) {
            this.stockStatus = {
                text: 'Dostępny',
                subtext: `${getOrderText(isBeforeCutoff)} ${formatDate(deliveryDate)}`,
                class: 'text-green-600 bg-green-100'
            };
        }
        // W przeciwnym razie produkt jest niedostępny
        else {
            this.stockStatus = {
                text: 'Produkt niedostępny',
                subtext: '',
                class: 'text-red-600 bg-red-100'
            };
        }
    },

    formatPrice(amount) {
        if (!amount) return '0,00 zł';
        return `${Number(amount).toFixed(2).replace('.', ',')} zł`;
    },

    sortAttributeValues() {
        const collator = new Intl.Collator(undefined, { numeric: true });
        Object.keys(this.availableAttributes).forEach(key => {
            if (this.availableAttributes[key].values) {
                this.availableAttributes[key].values.sort((a, b) =>
                    collator.compare(a.name, b.name)
                );
            }
        });
    },

    // Obsługa URL i historii.
    // Decyzja 6 hybryda (2026-06-16): gdy wariant kompletny i istnieje w variationsMap →
    // piszemy `?variant=ID` (zgodne z canonical w <head>, single source of truth dla SEO).
    // Gdy niekompletny lub nie znaleziono → fallback do `?pa_*` (dawne zachowanie, dla UX
    // gdy user wybrał część atrybutów).
    updateUrl() {
        if (!window.history?.replaceState) return;
        try {
            let newUrl = null;

            if (this.isCompleteVariation()) {
                const variation = this.findSelectedVariation();
                if (variation && variation.variation_id) {
                    newUrl = `${window.location.pathname}?variant=${variation.variation_id}`;
                }
            }

            if (!newUrl) {
                const searchParams = new URLSearchParams();
                Object.entries(this.selectedAttributes).forEach(([key, value]) => {
                    if (value) {
                        searchParams.set(key, value);
                    }
                });
                newUrl = `${window.location.pathname}?${searchParams.toString()}`;
            }

            window.history.replaceState({
                attributes: {...this.selectedAttributes}
            }, '', newUrl);
        } catch (error) {
            console.error('Error updating URL:', error);
        }
    },

    handlePopState(event) {
        if (event?.state?.attributes) {
            try {
                this.selectedAttributes = {...event.state.attributes};
                if (this.isCompleteVariation()) {
                    const selectedVariation = this.findSelectedVariation();
                    if (selectedVariation) {
                        this.fetchVariantData(selectedVariation.variation_id);
                    }
                } else {
                    this.resetToParentImages();
                }
            } catch (error) {
                console.error('Error handling popstate:', error);
                this.selectedAttributes = {};
                this.resetToParentImages();
            }
        }
    },

    checkUrlParameters() {
        const urlParams = new URLSearchParams(window.location.search);

        // Decyzja 6 hybryda (2026-06-16): jeśli URL wskazuje na ?variant=ID,
        // wczytaj wariant po ID i ustaw atrybuty z niego. Pokrywa wejścia z Google SERP
        // i bookmarki na konkretny wariant.
        const variantIdRaw = urlParams.get('variant');
        const variantId = variantIdRaw ? parseInt(variantIdRaw, 10) : 0;
        if (variantId > 0 && Array.isArray(this.variationsMap) && this.variationsMap.length) {
            const variation = this.variationsMap.find(v => v.variation_id === variantId);
            if (variation && variation.attributes) {
                this.selectedAttributes = { ...variation.attributes };
                // Sanity: jeśli wariant z URL jest OOS (0+0), przełącz na pierwszy dostępny
                // — ta sama logika co dla ?pa_* poniżej (Reguła Huberta 2026-06-11: zero popupów).
                if (typeof variation.kablowo_total === 'number' && variation.kablowo_total <= 0) {
                    const fallback = this.findFirstAvailableVariation(variation.attributes);
                    if (fallback) {
                        this.selectedAttributes = { ...fallback.attributes };
                        this.updateUrl();
                        this.fetchVariantData(fallback.variation_id);
                        return;
                    }
                }
                this.fetchVariantData(variation.variation_id);
                return;
            }
            // ?variant=ID nie znaleziono w mapie — milcząco fall-through do parsowania ?pa_*
        }

        // Ścieżka legacy: parsowanie ?pa_* (Allegro deeplinks, dawne bookmarki).
        const attributesFromUrl = {};
        urlParams.forEach((value, key) => {
            if (this.availableAttributes[key]) {
                attributesFromUrl[key] = value;
            }
        });

        if (Object.keys(attributesFromUrl).length > 0) {
            this.selectedAttributes = attributesFromUrl;
            if (this.isCompleteVariation()) {
                const selectedVariation = this.findSelectedVariation();
                // 2026-06-11: auto-skip jeśli URL wskazuje na wariant 0+0.
                // Dla przykładu: /produkt/patchcord-kat-5e-ftp/?pa_dlugosc-przewodu=20&pa_kolor=szary
                // gdzie szary 20m ma _stock_szczecin=0 i _supplier_stock=0.
                // Przełączamy na pierwszy dostępny wariant + ciche info.
                if (selectedVariation && typeof selectedVariation.kablowo_total === 'number' && selectedVariation.kablowo_total <= 0) {
                    const fallback = this.findFirstAvailableVariation(attributesFromUrl);
                    if (fallback) {
                        this.selectedAttributes = { ...fallback.attributes };
                        this.updateUrl();
                        this.fetchVariantData(fallback.variation_id);
                        // Ciche info - bez popup-toasta. Klient widzi że URL/UI się zmienił.
                        // Reguła Huberta 2026-06-11: zero popupów na initial load.
                        return;
                    }
                }
                if (selectedVariation) {
                    // Decyzja 6 hybryda: gdy weszliśmy z ?pa_*, przepisz URL na ?variant=ID
                    // (canonical match, single source of truth dla kolejnej nawigacji).
                    this.updateUrl();
                    this.fetchVariantData(selectedVariation.variation_id);
                }
            }
        }
    },

    // Obsługa zapytań HTTP
    async fetchWithRetry(url, options, attempt = 0) {
        try {
            const response = await fetch(url, options);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json();
        } catch (error) {
            if (attempt >= RETRY_ATTEMPTS) throw error;
            await new Promise(resolve =>
                setTimeout(resolve, RETRY_DELAY * Math.pow(2, attempt))
            );
            return this.fetchWithRetry(url, options, attempt + 1);
        }
    },

    // Czyszczenie zasobów
    cleanup() {
        try {
            // Czyścimy cache obrazów
            if (this.imageCache && typeof this.imageCache.clear === 'function') {
                this.imageCache.clear();
            }

            // Czyścimy oczekujące zapytania
            if (this.pendingRequests) {
                this.pendingRequests.forEach(request => {
                    if (request && request.signal && typeof request.signal.abort === 'function') {
                        request.signal.abort();
                    }
                });
                this.pendingRequests.clear();
            }

            // Usuwamy event listenery
            window.removeEventListener('popstate', this.handlePopState);
            window.removeEventListener('cable:lengthChanged', null);
            document.removeEventListener('keydown', this.handleKeyDown);
            window.removeEventListener('variant-selector:attributeSelected', null);

            this.loading = false;
        } catch (error) {
            console.warn('Error during cleanup:', error);
        }
    }
});

// Rejestracja Service Workera
// if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
//     window.addEventListener('load', () => {
//         navigator.serviceWorker
//             .register('/sw.js')
//             .then(registration => {
//                 console.log('ServiceWorker registration successful');
//             })
//             .catch(error => {
//                 console.error('ServiceWorker registration failed:', error);
//             });
//     });
// }

window.addEventListener('beforeunload', () => {
    try {
        const productHandlerInstance = Alpine.$data('productHandler');
        if (productHandlerInstance && typeof productHandlerInstance.cleanup === 'function') {
            productHandlerInstance.cleanup();
        }
    } catch (error) {
        console.warn('Cleanup during unload failed:', error);
    }
});

// Czyszczenie cache
window.addEventListener('unload', () => {
    if ('caches' in window) {
        caches.keys().then(cacheNames => {
            cacheNames.forEach(cacheName => {
                if (cacheName.startsWith('product-images-')) {
                    caches.delete(cacheName);
                }
            });
        });
    }
});

window.productHandler = productHandler;